import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { push, routerActions } from "connected-react-router";
import { bool, func, arrayOf, shape } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { isIOS } from "react-device-detect";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import * as dateUtils from "util/date";
import { alphabeticalSorting } from "util/servicePayments";
import useSchema from "hooks/useSchema";
import { actions as notificationActions } from "reducers/notification";
import { actions as afipPaymentLinkActions, selectors as afipPaymentLinkSelectors } from "reducers/afipPaymentLink";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Selector from "pages/_components/fields/formik/Selector";
import Checkbox from "pages/_components/Checkbox";
import ProductSelector from "pages/discounts/DiscountCreation/components/ProductSelector";
import CustomData from "pages/_components/CustomData/CustomData";
import FormattedAmount from "pages/_components/FormattedAmount";
import AmountField from "pages/_components/fields/formik/AmountField";
import InfoModal from "pages/_components/InfoModal";

const FORM_ID = "new.service.payment.link";

const NewPayment = ({
    isDesktop,
    fetching,
    dispatch,
    categories,
    entities,
    concepts,
    accounts,
    serviceIsAdhered,
    servicesPaymentDebt,
    addedServicePaymentData,
}) => {
    const formRef = useRef(null);
    const { stringRequired } = useSchema();
    const [alphanumericValidation, setAlphanumericValidation] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedEntityId, setSelectedEntityId] = useState(null);
    const [addServicesChecked, setAddServicesChecked] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([{ id: "1" }]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [amountValue, setAmountValue] = useState(false);
    const [shouldEnterAmount, setShouldEnterAmount] = useState(null);
    const [validateAmountRange, setValidateAmountRange] = useState(false);
    const [showReference, setShowReference] = useState(false);
    const [referenceText, setReferenceText] = useState("Referencia");
    const [validaterReferenceLength, setValidaterReferenceLength] = useState(false);
    const [minAmountValue, setMinAmountValue] = useState(null);
    const [maxAmountValue, setMaxAmountValue] = useState(null);
    const [minLengthReference, setMinLengthReference] = useState(null);
    const [maxLengthReference, setMaxLengthReference] = useState(null);
    const [minLengthAmount, setMinLengthAmount] = useState(1);
    const [maxLengthAmount, setMaxLengthAmount] = useState(12);
    const [manageDebtBase, setManageDebtBase] = useState(false);
    const [nextDueDateData, setNextDueDateData] = useState(null);
    const [amountUpdated, setAmountUpdated] = useState(false);
    const [displayModal, setDisplayModal] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [entityOptions, setEntityOptions] = useState([]);

    const initialValues = {
        category: null,
        entity: null,
        concept: null,
        paymentCode: null,
        alias: null,
        amountField: null,
        reference: null,
    };

    const handleResetEntitySelector = () => {
        formRef.current.setFieldValue("entity", "");
    };

    const handleResetConceptSelector = () => {
        formRef.current.setFieldValue("concept", "");
    };

    const resetSecondFormBox = () => {
        formRef.current.setFieldValue("alias", null);
        formRef.current.setFieldValue("amountField", null);
        formRef.current.setFieldValue("reference", null);
        setSelectedAccount(null);
    }

    const handleReset = () => {
        handleResetEntitySelector();
        handleResetConceptSelector();
        formRef.current.setFieldValue("category", "");
        formRef.current.setFieldValue("paymentCode", "");
        resetSecondFormBox();
        setDisplayModal(false);
    };

    useEffect(() => {
        if (categories.length > 0) {
            const aux = categories.map(({ id, description }) => ({
                value: id,
                label: description,
            }))
            setCategoryOptions(alphabeticalSorting(aux))
        }
    }, [categories]);

    useEffect(() => {
        if (entities.length > 0) {
            const aux = entities.map(({ id, description }) => ({
                value: id,
                label: description,
            }))
            setEntityOptions(alphabeticalSorting(aux))
        }
    }, [entities]);

    useEffect(() => {
        if (!addedServicePaymentData) {
            handleReset();
            dispatch(afipPaymentLinkActions.newServicePaymentPre());
        } else {
            dispatch(afipPaymentLinkActions.newServicePaymentPre({
                categoryId: addedServicePaymentData?.categoryId,
                entityId: addedServicePaymentData?.entityId,
                paymentCode: addedServicePaymentData?.codeLink || addedServicePaymentData?.paymentCode,
                paymentFromAdheredService: true,
            }));
        }
    }, [addedServicePaymentData]);

    useEffect(() => {
        if (categories.length > 0 && entities.length > 0 && addedServicePaymentData) {
            formRef.current.setFieldValue("category", addedServicePaymentData?.categoryId);
            setSelectedCategoryId(addedServicePaymentData?.categoryId);

            formRef.current.setFieldValue("entity", addedServicePaymentData?.entityId);
            setSelectedEntityId(addedServicePaymentData?.entityId);

            const paymentCode = addedServicePaymentData?.codeLink || addedServicePaymentData?.paymentCode;
            formRef.current.setFieldValue("paymentCode", paymentCode, false);

            if (addedServicePaymentData?.debtId) {
                setManageDebtBase(true);
                setNextDueDateData({
                    amount: addedServicePaymentData?.amount,
                    debtId: addedServicePaymentData?.debtId,
                });
            }
            setAmountUpdated(!amountUpdated);
        }
    }, [categories, entities]);


    useEffect(() => {
        const alphanumericWithSpacesValidation = configUtils.get("alphanumeric.blankSpaces.validationFormat", /^[a-zA-Z0-9\s]*$/);
        setAlphanumericValidation(alphanumericWithSpacesValidation);

        const minLengthAmountValue = configUtils.get("service.payment.amount.length.min", 1);
        const maxLengthAmountValue = configUtils.get("service.payment.amount.length.max", 12);
        setMinLengthAmount(minLengthAmountValue);
        setMaxLengthAmount(maxLengthAmountValue);

        dispatch(accountsActions.listAccounts());
    }, []);

    useEffect(() => {
        if (accounts?.length) {
            const formatOption = (account) => ({
                balance: { currency: account?.currency, quantity: account?.availableBalance },
                currency: account?.currency,
                frequentDestination: null,
                id: account?.productId,
                label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(-4)}`,
                minimumPayment: 0,
                permissions: Object.keys(account?.permissions),
                statementsLoan: null,
                totalPayment: 0,
                type: account?.accountType,
                accountNumber: account?.accountNumber,
            });

            setAvailableAccounts(accounts.filter((acc) => acc.currency === "ARS")?.map((acc) => formatOption(acc)));
        }
    }, [accounts]);

    useEffect(() => {
        if (selectedCategoryId && !addedServicePaymentData) {
            setHasSearched(false);
            setSelectedEntityId(null)
            handleResetEntitySelector();
            handleResetConceptSelector();
            dispatch(afipPaymentLinkActions.newServicePaymentPre({ categoryId: selectedCategoryId }));
        }
    }, [selectedCategoryId]);

    useEffect(() => {
        if (selectedEntityId) {
            setSelectedConcept(null);
            setHasSearched(false);
            handleResetConceptSelector();
            const entityIdx = entities.findIndex((e) => e.id === selectedEntityId);
            const manageDebtValue = entities[entityIdx]?.manageDebtBase;
            setManageDebtBase(manageDebtValue);
            dispatch(afipPaymentLinkActions.newServicePaymentPre({ entityId: selectedEntityId, manageDebtBase: manageDebtValue }));
        }
    }, [selectedEntityId]);

    useEffect(() => {
        if (concepts && concepts.length === 1) {
            formRef.current.setFieldValue("concept", concepts[0]?.id);
            setSelectedConcept(concepts[0]);
        }
    }, [concepts]);

    const formatReference = (str) => {
        if (!str || typeof str !== "string") {
            return "";
        }
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const resetState = () => {
        setShouldEnterAmount(false);
        setAmountValue(null);
        setValidateAmountRange(false);
        setShowReference(false);
        setReferenceText("Referencia");
        setValidaterReferenceLength(false);
        setMinAmountValue(null);
        setMaxAmountValue(null);
        setMinLengthReference(null);
        setMaxLengthReference(null);
        setAmountUpdated(!amountUpdated)
        setNextDueDateData(null);
    }

    useEffect(() => {
        if (!fetching && hasSearched) {
            if (manageDebtBase) {
                if (servicesPaymentDebt) {
                    if (servicesPaymentDebt.length > 0 && !nextDueDateData) {
                        const auxList = [];
                        servicesPaymentDebt.forEach((element) => {
                            const { entityId } = element;
                            if (entityId === selectedEntityId) {
                                auxList.push(element);
                            }
                        });
                        let debtData;
                        const debtIdx = auxList.findIndex(debt => debt.conceptId === selectedConcept?.id);
              
                        if (debtIdx !== -1) {
                            debtData = auxList[debtIdx];

                            setNextDueDateData(debtData);

                            setAmountValue(debtData?.amount);

                            if (selectedConcept?.paymentType === "IGUAL") {
                                setValidateAmountRange(false);
                                setShouldEnterAmount(false);
                                formRef.current.setFieldValue("amountField", {
                                    amount: debtData?.amount,
                                    currency: "ARS",
                                });
                            } else if (selectedConcept?.addAmount?.required) {
                                setShouldEnterAmount(true);
                                setValidateAmountRange(true);
                                switch (selectedConcept?.paymentType) {
                                    case "INDISTINTO":
                                        if (selectedConcept?.addAmount?.manageRange) {
                                            setMinAmountValue(parseFloat(selectedConcept?.addAmount?.amount?.min));
                                            setMaxAmountValue(parseFloat(selectedConcept?.addAmount?.amount?.max));
                                        } else {
                                            setValidateAmountRange(false);
                                        }
                                        break;
                                    case "MENOR_IGUAL":
                                        setMaxAmountValue(debtData?.amount);
                                        setMinAmountValue(1.0);
                                        break;
                                    case "MAYOR_IGUAL":
                                        setMinAmountValue(debtData?.amount);
                                        setMaxAmountValue(null);
                                        break;
                                    default:
                                        break;
                                }
                            }

                            if (serviceIsAdhered && !addedServicePaymentData) {
                                setDisplayModal(true);
                            }

                        } else {
                            dispatch(
                                notificationActions.showNotification(i18n.get("", "El Código de pago no se corresponde al concepto seleccionado"), "error", [
                                    "servicesPayment",
                                ]),
                            );
                            setHasSearched(false);
                        }
                    } else if (servicesPaymentDebt.length === 0) {
                        dispatch(
                            notificationActions.showNotification(i18n.get("service.payment.no.debt.message", "El servicio que estás intentando pagar, ya fue abonado previamente"), "error", [
                                "servicesPayment",
                            ]),
                        );
                        setHasSearched(false);
                    }

                    setShowReference(selectedConcept.referenceText?.required);
                    setReferenceText(formatReference(selectedConcept?.referenceText?.text || "Referencia"));
                    setValidaterReferenceLength(selectedConcept.referenceText?.length?.validate);
                    setMinLengthReference(selectedConcept?.referenceText?.length?.min || null);
                    setMaxLengthReference(selectedConcept?.referenceText?.length?.max || null);
                } else {
                    setHasSearched(false);
                    resetState();
                }
            } else if (selectedConcept?.addAmount?.required && selectedConcept?.paymentType === "INDISTINTO") {
                setShouldEnterAmount(true);
                if (selectedConcept?.addAmount?.manageRange) {
                    setValidateAmountRange(true);
                    setMinAmountValue(parseFloat(selectedConcept?.addAmount?.amount?.min));
                    setMaxAmountValue(parseFloat(selectedConcept?.addAmount?.amount?.max));
                } else {
                    setValidateAmountRange(false);
                }
                setShowReference(selectedConcept.referenceText?.required);
                setReferenceText(formatReference(selectedConcept?.referenceText?.text || "Referencia"));
                setValidaterReferenceLength(selectedConcept.referenceText?.length?.validate);
                setMinLengthReference(selectedConcept?.referenceText?.length?.min || null);
                setMaxLengthReference(selectedConcept?.referenceText?.length?.max || null);
            }
        } else {
            resetState();
        }
    }, [hasSearched, fetching, nextDueDateData]);

    const handleBack = () => {
        dispatch(routerActions.goBack());
        handleReset();
        dispatch(afipPaymentLinkActions.resetServicePaymentInfo());
        dispatch(afipPaymentLinkActions.cleanAddedServicePaymentData());
    };

    const title = !addedServicePaymentData ? "new.service.payment.link.title" : "pay.added.service.link.title";
    const defaultTitle = !addedServicePaymentData ? "Nuevo Pago de Servicios" : "Pagar Servicio Adherido";

    const renderHeader = () => (
        <>
            <CustomProductHead
                titleId=""
                backLinkTo="/servicesPayment"
                className="accounts"
                onBack={() => handleBack()}>
                <div className="toolbar-item view-title">
                    <ViewTitle id={title} defaultValue={defaultTitle} />
                </div>
            </CustomProductHead>
        </>
    );

    const getAmountValidationMessage = () => {
        switch (selectedConcept?.paymentType) {
            case "MENOR_IGUAL":
                return i18n
                    .get("services.payment.link.amount.range.validation")
                    .replace("#{MIN}", minAmountValue)
                    .replace("#{MAX}", maxAmountValue);
            case "MAYOR_IGUAL":
                return i18n.get("services.payment.link.amount.min.validation").replace("#{MIN}", minAmountValue);
            case "INDISTINTO":
                return i18n
                    .get("services.payment.link.amount.range.validation")
                    .replace("#{MIN}", minAmountValue)
                    .replace("#{MAX}", maxAmountValue);
            default:
                return i18n.get("services.payment.link.amount.error", "Monto no permitido");
        }
    };

    const validateAmount = (amount) => {
        switch (selectedConcept?.paymentType) {
            case "MENOR_IGUAL":
                return parseFloat(minAmountValue) <= amount && amount <= parseFloat(maxAmountValue);
            case "MAYOR_IGUAL":
                return parseFloat(minAmountValue) <= amount;
            case "INDISTINTO":
                return parseFloat(minAmountValue) <= amount && amount <= parseFloat(maxAmountValue);
            default:
                return false;
        }
    };

    const validation = Yup.lazy(() => {
        const validate = {
            category: stringRequired.nullable(),
            entity: stringRequired.nullable(),
            concept: stringRequired.nullable(),
            paymentCode: stringRequired,
            alias: Yup.string()
                .notRequired()
                .nullable()
                .matches(
                    alphanumericValidation,
                    i18n.get(
                        "forms.validation.alphanumeric.invalid.format",
                        "El campo ingresado debe ser alfanumérico",
                    ),
                ),
            amountField: Yup.object().shape({
                amount: validateAmountRange
                    ? stringRequired
                        .min(
                            minLengthAmount,
                            i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthAmount),
                        )
                        .max(
                            maxLengthAmount,
                            i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthAmount),
                        )
                        .test("amount", getAmountValidationMessage(), (value) => !!value && validateAmount(value))
                    : stringRequired
                        .min(
                            minLengthAmount,
                            i18n.get("comex.tax.minLength.error").replace("#{MIN}", minLengthAmount),
                        )
                        .max(
                            maxLengthAmount,
                            i18n.get("comex.tax.maxLength.error").replace("#{MAX}", maxLengthAmount),
                        )
                        .test("amount", i18n.get("services.payment.link.amount.error", "Monto no permitido"), (value) => !!value && value > parseFloat("0,00")),
            }),
            reference: validaterReferenceLength
                ? stringRequired
                    .nullable()
                    .test(
                        "reference",
                        i18n
                            .get("services.payment.link.reference.range.validation")
                            .replace("#{FIELD}", referenceText)
                            .replace("#{MIN}", minLengthReference)
                            .replace("#{MAX}", maxLengthReference),
                        (value) =>
                            !!value && minLengthReference <= value.length && value.length <= maxLengthReference,
                    )
                    .matches(
                        alphanumericValidation,
                        i18n.get(
                            "forms.validation.alphanumeric.invalid.format",
                            "El campo ingresado debe ser alfanumérico",
                        ),
                    )
                : Yup.string().nullable(),
        };
        return Yup.object().shape(validate);
    });

    const handleSubmit = (values) => {
        const { category, entity, concept, paymentCode, alias, reference, amountField } = values;
        const categoryIdx = categories.findIndex((c) => c.id === category);
        const categoryName = categoryIdx !== -1 ? categories[categoryIdx].description : "-";
        const entityIdx = entities.findIndex((e) => e.id === entity);
        const entityName = entityIdx !== -1 ? entities[entityIdx].description : "-";
        const conceptIdx = concepts.findIndex((c) => c.id === concept);
        const conceptName = conceptIdx !== -1 ? concepts[conceptIdx].description : "-";
        const account = availableAccounts.filter((a) => a.id === selectedAccount)[0];

        const servicePaymentData = {
            categoryId: category,
            entityId: entity,
            conceptId: concept,
            paymentCode,
            categoryName,
            entityName,
            conceptName,
            referenceText,
            alias,
            reference,
            amountField,
            account,
            addServicesChecked,
            conceptAmountValue: amountValue ? amountValue : null,
            debtId: manageDebtBase ? nextDueDateData?.debtId : null,
        };
        dispatch(afipPaymentLinkActions.setServicesPaymentData(servicePaymentData));
        dispatch(push("/newServicePayment/confirm"));
    };

    const searchIsNotAllowed = (values) => {
        const { category, entity, concept, paymentCode } = values;
        return !category || !entity || !concept || !paymentCode;
    };

    const continueIsNotAllowed = (values) => {
        const { reference } = values;
        const validReference = validaterReferenceLength ? reference : true;
        return searchIsNotAllowed(values) || !validReference || !selectedAccount;
    };

    const search = (values) => {
        const { category, entity, paymentCode } = values;
        resetSecondFormBox();
        dispatch(
            afipPaymentLinkActions.newServicePaymentPre({
                entityId: entity,
                categoryId: category,
                paymentCode,
                manageDebtBase,
            }),
        );
        setHasSearched(true);
    };

    const validAmount = (accountId, amount) => {
        const selected = availableAccounts.find((_account) => _account.id === accountId);
        return amount <= selected?.balance?.quantity;
    };

    const selectAccount = (accountId, amount) => {
        if (!validAmount(accountId, amount)) {
            dispatch(
                notificationActions.showNotification(i18n.get("insuficient.amount.error"), "error", [
                    "servicesPayment",
                ]),
            );
        } else {
            setSelectedAccount(accountId);
        }
    };

    const handleChangeConcept = (value) => {
        setHasSearched(false);
        const conceptIdx = concepts.findIndex((c) => c.id === value);
        setSelectedConcept(concepts[conceptIdx]);
        setNextDueDateData(null);
    };

    const handleOnChangePaymentCode = () => {
        if (hasSearched) {
            setHasSearched(false);
            setNextDueDateData(null);
        }
    };

    const placeholderCategory = isDesktop ? "Seleccioná el Rubro que corresponda" : "Seleccioná el Rubro";
    const placeholderCategoryId = isDesktop ? "services.payment.link.category.selector.placeholder" : "";
    const placeholderAlias = isDesktop ? "Ingresá el Alias con el que vas a agendar el Servicio" : "Ingresá el Alias";
    const placeholderAliasId = isDesktop ? "services.payment.link.alias.placeholder" : "";

    const onCloseModal = () => {
        setDisplayModal(false);
    };

    const createBodyModal = () => (
        <div className="service-adhered-modal-container">
            <h2>{i18n.get("services.payment.link.adheredService.modalInfo.1", "Este sevicio ya se encuentra agendado para su próximo pago.")}</h2>
            <h2>{i18n.get("services.payment.link.adheredService.modalInfo.2", "Igualmente, si necesitás hacer un nuevo pago, podés continuar desde acá.")}</h2>
            <div className="service-adhered-modal-buttons-container">
                <Button
                    className="btn btn-primary btn-block service-adhered-modal-button"
                    label="global.continue"
                    loading={false}
                    onClick={onCloseModal}
                    id="save-code-button"
                />
                <Button
                    className="btn btn-outline btn-block service-adhered-modal-button"
                    label="global.back"
                    loading={false}
                    onClick={() => {
                        onCloseModal();
                        handleBack();
                    }}
                    id="save-code-button"
                />
            </div>
        </div>
    );

    return (
        <>
            <Notification scopeToShow="servicesPayment" />
            {renderHeader()}
            <MainContainer>
                {displayModal && (
                    <InfoModal
                        noAcceptButton
                        noCancelButton
                        bodyInfoText="texto"
                        useScroll
                        body={createBodyModal}
                        onClick={onCloseModal}
                        dialogClassName="service-adhered-modal-border"
                    />
                )}
                <div className="container">
                    <Formik
                        initialValues={initialValues}
                        ref={formRef}
                        validationSchema={validation}
                        onSubmit={(values, formikBag) => handleSubmit(values, formikBag)}>
                        {(allFormik) => (
                            <Form>
                                <div className="box box-shadow">
                                    <Row style={{ display: "flex", alignContent: "space-between", padding: "10px" }}>
                                        <Col xs={12} md={4}>
                                            <Row>
                                                <Col xs={12} md={12}>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="category"
                                                        options={
                                                            categoryOptions
                                                                ? categoryOptions
                                                                : []
                                                        }
                                                        label={i18n.get(
                                                            "services.payment.link.category.selector.label",
                                                            "Rubro",
                                                        )}
                                                        placeholder={i18n.get(
                                                            placeholderCategoryId,
                                                            placeholderCategory,
                                                        )}
                                                        onChange={(value) => setSelectedCategoryId(value)}
                                                        disabled={addedServicePaymentData && addedServicePaymentData?.categoryId}
                                                        searchable={!isIOS}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} md={12}>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="entity"
                                                        options={
                                                            entityOptions
                                                                ? entityOptions
                                                                : []
                                                        }
                                                        label={i18n.get(
                                                            "services.payment.link.entity.selector.label",
                                                            "Empresa",
                                                        )}
                                                        placeholder={i18n.get(
                                                            "services.payment.link.entity.selector.placeholder",
                                                            "Elegí la Empresa",
                                                        )}
                                                        disabled={!selectedCategoryId || fetching || (addedServicePaymentData && addedServicePaymentData?.entityId)}
                                                        onChange={(value) => setSelectedEntityId(value)}
                                                        searchable={!isIOS}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} md={12}>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="concept"
                                                        options={
                                                            concepts
                                                                ? concepts.map(({ id, description }) => ({
                                                                    value: id,
                                                                    label: description,
                                                                }))
                                                                : []
                                                        }
                                                        label={i18n.get(
                                                            "new.service.payment.link.concept.label",
                                                            "Concepto",
                                                        )}
                                                        placeholder={i18n.get(
                                                            "new.service.payment.link.concept.placeholder",
                                                            "Indicá el Concepto",
                                                        )}
                                                        disabled={!selectedEntityId || fetching}
                                                        onChange={(value) => handleChangeConcept(value)}
                                                        searchable={!isIOS}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} md={12}>
                                                    <Field
                                                        component={TextField}
                                                        labelText="services.payment.link.paymentCode.label"
                                                        labelTextDefault="Código de pago"
                                                        placeholderText={i18n.get(
                                                            "services.payment.link.paymentCode.placeholder",
                                                            "Introducí el Código de pago",
                                                        )}
                                                        idForm={FORM_ID}
                                                        name="paymentCode"
                                                        type="text"
                                                        pattern="[0-9]*"
                                                        maxLength={50}
                                                        minLength={0}
                                                        disabled={addedServicePaymentData && (addedServicePaymentData?.codeLink || addedServicePaymentData?.paymentCode)}
                                                        handleOnChange={() => handleOnChangePaymentCode()}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <div className="grid-table form-actions">
                                        <div className="grid-table-row sm:flex md:grid-cols-12">
                                            <div className="grid-table-data md:col-start-5 md:col-span-4">
                                                <Button
                                                    className="btn btn-primary btn-block small"
                                                    label="global.search"
                                                    loading={fetching}
                                                    block
                                                    disabled={searchIsNotAllowed(allFormik.values)}
                                                    onClick={() => search(allFormik.values)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="py-6" />
                                {hasSearched && !fetching && (
                                    <div>
                                        <div className="box box-shadow">
                                            <Row>
                                                {!serviceIsAdhered && !addedServicePaymentData && (
                                                    <>
                                                        <Col xs={12} md={12}>
                                                            <Field
                                                                component={TextField}
                                                                labelText="services.payment.link.alias.label"
                                                                labelTextDefault="Alias (opcional)"
                                                                placeholderText={i18n.get(
                                                                    placeholderAliasId,
                                                                    placeholderAlias,
                                                                )}
                                                                idForm={FORM_ID}
                                                                name="alias"
                                                                type="text"
                                                                maxLength={25}
                                                                minLength={0}
                                                            />
                                                        </Col>
                                                        <Row>
                                                            <Field
                                                                component={Checkbox}
                                                                idForm={FORM_ID}
                                                                name="addService"
                                                                labelText="payments.service.schedule"
                                                                labelTextProps={{ defaultValue: "Agendar servicio" }}
                                                                onChange={(value) => setAddServicesChecked(value)}
                                                            />
                                                        </Row>
                                                        <div className="py-6" />
                                                    </>
                                                )}
                                                <Row xs={12} md={12}>
                                                    {amountValue && (
                                                        <Col xs={6} md={6}>
                                                            <CustomData
                                                                titleText="services.payment.link.amount.debt"
                                                                titleTextType="I18N"
                                                                valueText={
                                                                    <FormattedAmount
                                                                        className="data-label"
                                                                        currency="ARS"
                                                                        quantity={amountValue}
                                                                    />
                                                                }
                                                                valueClass="concept-amount"
                                                                titleClass="font-weight-600"
                                                            />
                                                        </Col>
                                                    )}
                                                    {shouldEnterAmount && (
                                                        <Col xs={6} md={6}>
                                                            <Field
                                                                component={AmountField}
                                                                labelText={i18n.get(
                                                                    "services.payment.link.amount.label",
                                                                    "Monto a abonar",
                                                                )}
                                                                idForm={FORM_ID}
                                                                disableSelect
                                                                name="amountField"
                                                                data={{
                                                                    options: [
                                                                        {
                                                                            id: 1,
                                                                            label: i18n.get("currency.label.ARS"),
                                                                        },
                                                                    ],
                                                                }}
                                                                clearable={false}
                                                                fixedDecimalScale
                                                                decimalLength={2}
                                                                inputHasChange={() => setAmountUpdated(!amountUpdated)}
                                                            />
                                                        </Col>
                                                    )}
                                                </Row>
                                                <div className="py-5" />
                                                {showReference && (
                                                    <Col xs={12} md={12}>
                                                        <Field
                                                            component={TextField}
                                                            labelText=""
                                                            labelTextDefault={referenceText}
                                                            hidePlaceholder
                                                            idForm={FORM_ID}
                                                            name="reference"
                                                            type="text"
                                                            maxLength={32}
                                                            minLength={0}
                                                        />
                                                    </Col>
                                                )}
                                                <Col xs={12} md={12}>
                                                    <ProductSelector
                                                        name="debitAccount"
                                                        idField="discount-acreditation-account"
                                                        label={i18n.get(
                                                            "services.payment.link.account.label",
                                                            "Cuenta débito",
                                                        )}
                                                        placeholder={i18n.get(
                                                            "services.payment.link.account.placeholder",
                                                            "Seleccioná cuenta de origen",
                                                        )}
                                                        data={{ options: availableAccounts || [] }}
                                                        onChange={(value) =>
                                                            selectAccount(value, allFormik.values?.amountField?.amount)
                                                        }
                                                        value={selectedAccount}
                                                        disabled={
                                                            !allFormik.values?.amountField?.amount || (validateAmountRange & !validateAmount(allFormik.values?.amountField?.amount))
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                        <div className="py-6" />
                                        <div className="grid-table form-actions">
                                            <div className="grid-table-row sm:flex md:grid-cols-12">
                                                <div className="grid-table-data md:col-start-5 md:col-span-4">
                                                    <Button
                                                        className="btn btn-primary btn-block small"
                                                        label="global.continue"
                                                        loading={fetching}
                                                        block
                                                        type="submit"
                                                        disabled={continueIsNotAllowed(allFormik.values)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
        </>
    );
};

NewPayment.propTypes = {
    isDesktop: bool.isRequired,
    dispatch: func.isRequired,
    fetching: bool.isRequired,
    serviceIsAdhered: bool.isRequired,
    categories: arrayOf(shape({})).isRequired,
    entities: arrayOf(shape({})).isRequired,
    concepts: arrayOf(shape({})).isRequired,
    accounts: arrayOf(shape({})).isRequired,
    servicesPaymentDebt: arrayOf(shape({})).isRequired,
    addedServicePaymentData: shape({}).isRequired,
};

const mapStateToProps = (state) => ({
    fetching: afipPaymentLinkSelectors.getFetching(state),
    categories: afipPaymentLinkSelectors.getCategories(state),
    entities: afipPaymentLinkSelectors.getEntities(state),
    concepts: afipPaymentLinkSelectors.getConcepts(state),
    serviceIsAdhered: afipPaymentLinkSelectors.serviceIsAdhered(state),
    accounts: accountsSelectors.getAccounts(state),
    servicesPaymentDebt: afipPaymentLinkSelectors.servicesPaymentDebt(state),
    addedServicePaymentData: afipPaymentLinkSelectors.getAddedServicePaymentData(state),
});

export default connect(mapStateToProps)(NewPayment);
